/**
 * Renders a governed document from its specification.
 *
 * The document is a list of typed sections rather than markup the agent wrote,
 * which is the whole reason it can be trusted: a chart section carries the
 * semantic query behind it and is drawn by the same component the workspace
 * uses, and each figure in a band is read from a certified member. The agent
 * chose the structure and the words; it did not choose the numbers.
 *
 * Rows arrive on the agent stream as previews so the document paints at once. A
 * section that has been persisted carries a panel id, which is what lets one
 * chart be refreshed on its own without re-running the rest of the document.
 */
import { RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState, type CSSProperties } from "react";
import { ApiError, chartsApi } from "../services/api";
import type { ArtifactComposedEvent } from "../services/agui";
import { formatValue } from "../services/chartOptions";
import { readableText } from "../services/displayText";
import type {
  ArtifactSection,
  DocumentChartSection,
  KpiTile,
  PanelResult,
} from "../types";
import { ChartView } from "./ChartView";
import { Markdown } from "./Markdown";

interface ArtifactDocumentProps {
  event: ArtifactComposedEvent;
  /** Shown in the dateline. The document is a point-in-time claim. */
  preparedOn?: string;
}

/** A stable key for a section, since a spec carries no section ids. */
function sectionKey(section: ArtifactSection, index: number): string {
  return `${section.kind}-${index}`;
}

/**
 * Turns a streamed preview into a panel result.
 *
 * Matched by section index because previews are produced before the server
 * assigns panel ids, and the API preserves section order when it saves. This is
 * the same arrangement `ComposedDashboard` uses for a dashboard's panels.
 */
function previewResults(
  event: ArtifactComposedEvent,
): Map<number, PanelResult> {
  const byIndex = new Map<number, PanelResult>();
  for (const preview of event.spec.previews ?? []) {
    if (!preview.rows.length) continue;
    const columns = [
      ...new Set(preview.rows.flatMap((row) => Object.keys(row))),
    ];
    byIndex.set(preview.sectionIndex, {
      panelId: `section-${preview.sectionIndex}`,
      status: "ok",
      columns,
      rows: preview.rows,
      rowCount: preview.rowCount,
      truncated: !preview.complete,
      units: preview.units,
      provenance: null,
      error: null,
    });
  }
  return byIndex;
}

/**
 * A figure the contract could not serve renders as unavailable rather than zero.
 *
 * Zero is a number somebody may act on. An empty tile is not, which is the
 * honest rendering of a member the governed layer declined to answer for.
 */
function tileValue(tile: KpiTile): string {
  if (tile.value === null || tile.value === "") return "Unavailable";
  return formatValue(tile.value, tile.metric);
}

function KpiBand({ title, tiles }: { title: string; tiles: KpiTile[] }) {
  // Every figure in the band is sized to fit the longest one, so a billion
  // stays inside its tile and the band still reads at one size.
  const longest = Math.max(8, ...tiles.map((tile) => tileValue(tile).length));
  return (
    <section className="doc-section doc-kpi" aria-label={title || "Figures"}>
      {title && <h2 className="doc-section-title">{readableText(title)}</h2>}
      <div
        className="doc-kpi-band"
        style={{ "--kpi-chars": longest } as CSSProperties}
      >
        {tiles.map((tile, index) => (
          <div className="doc-kpi-tile" key={`${tile.label}-${index}`}>
            <span className="doc-kpi-label">{readableText(tile.label)}</span>
            <strong
              className={
                tile.value === null ? "doc-kpi-value unavailable" : "doc-kpi-value"
              }
              title={tileValue(tile)}
            >
              {tileValue(tile)}
            </strong>
            {tile.caption && (
              <span className="doc-kpi-caption">{readableText(tile.caption)}</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function ChartSection({
  section,
  index,
  result,
  onRefreshed,
}: {
  section: DocumentChartSection;
  index: number;
  result: PanelResult | undefined;
  onRefreshed: (index: number, next: PanelResult) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const panelId = section.panelId;

  async function refresh() {
    if (!panelId || busy) return;
    setBusy(true);
    setError("");
    try {
      onRefreshed(index, await chartsApi.refresh(panelId));
    } catch (cause: unknown) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "That section could not be refreshed.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="doc-section doc-chart">
      <div className="doc-section-head">
        <h2 className="doc-section-title">{readableText(section.title)}</h2>
        {/* Only once persisted. Before that there is no stored query to re-run,
            and a button that cannot work is worse than no button. */}
        {panelId && (
          <button
            type="button"
            className="icon-button"
            onClick={refresh}
            disabled={busy}
            title="Refresh this section"
            aria-label={`Refresh ${section.title}`}
          >
            <RefreshCw size={15} />
          </button>
        )}
      </div>
      {section.narrative && (
        <p className="doc-section-narrative">{readableText(section.narrative)}</p>
      )}
      <ChartView
        panelId={panelId ?? `section-${index}`}
        chartType={section.chartType ?? (section.kind === "table" ? "table" : "bar")}
        encoding={section.encoding}
        result={result}
        loading={busy}
      />
      {result?.truncated && (
        <p className="doc-section-note">
          Showing the first {result.rows.length} of {result.rowCount} rows.
        </p>
      )}
      {error && (
        <p className="doc-section-note error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

export function ArtifactDocument({ event, preparedOn }: ArtifactDocumentProps) {
  const [refreshed, setRefreshed] = useState<Map<number, PanelResult>>(
    new Map(),
  );
  const previews = useMemo(() => previewResults(event), [event]);

  const onRefreshed = useCallback((index: number, next: PanelResult) => {
    setRefreshed((current) => new Map(current).set(index, next));
  }, []);

  const sections = event.spec.sections ?? [];
  const dateline = preparedOn ?? new Date().toISOString().slice(0, 10);

  return (
    <article className="artifact-document">
      <header className="doc-header">
        <div>
          <p className="doc-kicker">Governed document</p>
          <h1 className="doc-title">{event.title || event.spec.title}</h1>
        </div>
        <dl className="doc-dateline">
          <div>
            <dt>Prepared on</dt>
            <dd>{dateline}</dd>
          </div>
          <div>
            <dt>Version</dt>
            <dd>{event.version}</dd>
          </div>
          {/* The contract behind every figure in the document. Printed on the
              document itself because a version sent without it cannot be
              checked later. */}
          <div>
            <dt>Contract</dt>
            <dd>
              {event.spec.contextId}
              {event.spec.contextVersion ? ` ${event.spec.contextVersion}` : ""}
            </dd>
          </div>
        </dl>
      </header>

      {sections.map((section, index) => {
        const key = sectionKey(section, index);
        if (section.kind === "headline") {
          return (
            <section className="doc-section doc-headline" key={key}>
              <h2 className="doc-editorial">{readableText(section.headline)}</h2>
              {section.body && <Markdown text={section.body} />}
            </section>
          );
        }
        if (section.kind === "kpi") {
          return <KpiBand key={key} title={section.title} tiles={section.tiles} />;
        }
        if (section.kind === "findings") {
          return (
            <section className="doc-section doc-findings" key={key}>
              {section.title && (
                <h2 className="doc-section-title">{readableText(section.title)}</h2>
              )}
              <div className="doc-findings-grid">
                {section.items.map((item, position) => (
                  <div className="doc-finding" key={`${item.heading}-${position}`}>
                    <strong>{readableText(item.heading)}</strong>
                    <p>{readableText(item.detail)}</p>
                  </div>
                ))}
              </div>
            </section>
          );
        }
        return (
          <ChartSection
            key={key}
            section={section}
            index={index}
            result={refreshed.get(index) ?? previews.get(index)}
            onRefreshed={onRefreshed}
          />
        );
      })}
    </article>
  );
}

export default ArtifactDocument;
